'use client'

import { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { Button, Card, Icon, Image, Spacer, Text } from '@vinted/web-ui'
import { X16 } from '@vinted/monochrome-icons'

import { toBase64 } from '@marketplace-web/shared/document'
import {
  FormDocumentFileModel,
  PaymentsIdentityDocumentIdentifier,
  PaymentsIdentityDocumentConfigurationModel,
} from '@marketplace-web/pay-identity/payments-identity-data'
import { KybFormFields } from '@marketplace-web/pay-identity/pay-identity-kyc-data'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import FileSelect from './FileSelect'

const NOT_SUPPORTED_FILE_TYPES = ['application/pdf']

type Props = {
  configuration: PaymentsIdentityDocumentConfigurationModel
  onError: (error: string) => void
}

const DocumentSelector = ({ configuration, onError }: Props) => {
  const translate = useTranslate('kyc_form.a11y.identity_document')

  const { clearErrors, setValue, watch, register } = useFormContext()

  const initialValue = useMemo(
    () => ({
      type: configuration.type,
      files: configuration.files.map(file => ({
        identifier: file.identifier,
        file: null,
        mediaType: null,
        body: null,
      })),
    }),
    [configuration],
  )

  const identityDocument = watch(KybFormFields.IdentityDocument, initialValue)

  useEffect(() => {
    const init = () => {
      register(KybFormFields.IdentityDocument)

      setValue(KybFormFields.IdentityDocument, {
        type: configuration.type,
        files: configuration.files.map(file => ({
          identifier: file.identifier,
        })),
      })
      clearErrors(KybFormFields.IdentityDocument)
    }

    if (!configuration) return

    init()
  }, [register, configuration, clearErrors, setValue, initialValue])

  const formatIdentityDocumentFile = async (
    file: File,
    identifier: PaymentsIdentityDocumentIdentifier,
  ) => {
    try {
      const fileInBase64 = await toBase64({ file, removeMetadata: true })

      return {
        file,
        identifier,
        mediaType: file.type,
        body: fileInBase64 || null,
      }
    } catch (ex) {
      return null
    }
  }

  const handleFileChange =
    (identifier: PaymentsIdentityDocumentIdentifier) => async (file: File | null) => {
      if (!file) return

      const uploadedFile = await formatIdentityDocumentFile(file, identifier)

      const updatedIdentityDocuments = {
        type: configuration.type,
        files: identityDocument.files.map(identityDocumentFile => {
          if (identityDocumentFile.identifier === identifier && uploadedFile) {
            return uploadedFile
          }

          return identityDocumentFile
        }),
      }

      clearErrors(KybFormFields.IdentityDocument)

      setValue(KybFormFields.IdentityDocument, updatedIdentityDocuments)
    }

  const handleFileSelectorError = (error: string) => {
    onError(error)
  }

  const deleteFile = (identifier: PaymentsIdentityDocumentIdentifier) => () => {
    const updatedIdentityDocuments = {
      type: configuration.type,
      files: identityDocument.files.map(identityDocumentFile => {
        if (identityDocumentFile.identifier === identifier) {
          return {
            ...identityDocumentFile,
            body: null,
          }
        }

        return identityDocumentFile
      }),
    }

    setValue(KybFormFields.IdentityDocument, updatedIdentityDocuments)
  }

  const getSupportedFileTypes = () => {
    return (
      configuration?.mediaTypes
        ?.filter(type => !NOT_SUPPORTED_FILE_TYPES.includes(type))
        .join(',') || undefined
    )
  }

  const getFileUploadNote = (identifier: PaymentsIdentityDocumentIdentifier) => {
    const fileConfiguration = configuration.files.filter(file => file.identifier === identifier)

    return fileConfiguration[0]?.uploadNote || null
  }

  const renderEmptyState = (file: FormDocumentFileModel) => {
    const uploadNote = getFileUploadNote(file.identifier)

    return (
      <div className="u-text-center u-ui-margin-large">
        <FileSelect
          maximumFileSize={configuration.maximumFileSize}
          supportedTypes={getSupportedFileTypes()}
          onChange={handleFileChange(file.identifier)}
          title={uploadNote}
          onError={handleFileSelectorError}
        />
        <Spacer size={Spacer.Size.Large} />
        {uploadNote && (
          <Text
            as="h4"
            type={Text.Type.Caption}
            text={uploadNote}
            alignment={Text.Alignment.Center}
          />
        )}
      </div>
    )
  }

  const renderPhoto = (file: FormDocumentFileModel) => {
    if (!file.body || !file.mediaType) return null

    const src = `data:${file.mediaType};base64,${file.body}`

    return (
      <>
        <Image
          src={src}
          size={Image.Size.X4Large}
          styling={Image.Styling.Rounded}
          alt={translate('uploaded_document')}
        />
        <div className="identity-document__document-delete-button">
          <Button
            testId={`identity-document-delete-button-${file.identifier}`}
            icon={
              <Icon
                name={X16}
                color={Icon.Color.GreyscaleLevel1}
                aria={{ 'aria-label': translate('buttons.delete') }}
              />
            }
            size={Button.Size.Small}
            inverse
            styling={Button.Styling.Filled}
            inline
            onClick={deleteFile(file.identifier)}
          />
        </div>
      </>
    )
  }

  const renderFile = (file: FormDocumentFileModel, index: number) => {
    return (
      <div key={index} className="u-ui-margin-right-large u-ui-margin-bottom-large">
        <Card>
          <div className="identity-document__uploader-file-content u-overflow-hidden">
            {file.body ? renderPhoto(file) : renderEmptyState(file)}
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="identity-document__uploader-container u-flex-wrap">
      {identityDocument?.files?.map(renderFile)}
    </div>
  )
}

export default DocumentSelector
